import React, { useState, useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, MessageCircle, Eye, Share2, Activity } from 'lucide-react';
import { shortTimeAgo } from '../utils/timeUtils';

/**
 * FeedCard Component - Optimized for Engagement & Performance
 * 
 * Key Features:
 * - 2-line content preview with "Read more" expansion
 * - Bold hook/title for scroll-stopping effect
 * - Optimistic UI updates for instant feedback
 * - Smooth animations and micro-interactions
 * - 12px/16px spacing rhythm for clarity
 * - React.memo for scroll performance
 */

const FeedCard = memo(({ item, onLike, onDislike, onShare, onImpression, onClick, debugMode = false }) => {
  const [impressionLogged, setImpressionLogged] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [localLikes, setLocalLikes] = useState(item.like_count || 0);
  const [localDislikes, setLocalDislikes] = useState(item.dislike_count || 0);
  const [userVote, setUserVote] = useState(null); // 'like', 'dislike', or null
  const [isAnimating, setIsAnimating] = useState(false);
  
  const isQuestion = item.content_type === 'question';
  const isAnswer = item.content_type === 'answer';

  // Log impression when card becomes visible
  useEffect(() => {
    if (!impressionLogged && onImpression) {
      onImpression(item);
      setImpressionLogged(true);
    }
  }, [impressionLogged, item, onImpression]);

  // Extract content preview (strip HTML, decode entities)
  const getContentPreview = (html, maxLength = 150) => {
    if (!html) return '';
    
    // Strip HTML tags
    let text = html.replace(/<[^>]*>/g, '').trim();
    
    // Decode HTML entities
    const tempElement = document.createElement('textarea');
    tempElement.innerHTML = text;
    text = tempElement.value;
    
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Split content into hook and preview for better hierarchy
  const getContentParts = (content) => {
    if (!content) return { hook: '', preview: '' };
    
    const fullText = getContentPreview(content, 300);
    const sentences = fullText.split(/[.!?]+/).filter(s => s.trim());
    
    if (sentences.length === 0) return { hook: fullText, preview: '' };
    
    const hook = sentences[0].trim() + (fullText.includes('.') ? '.' : '');
    const preview = sentences.slice(1).join('. ').trim();
    
    return { hook, preview: preview ? preview : '' };
  };

  // Handle optimistic like with animation
  const handleLikeClick = () => {
    if (userVote === 'like') {
      // Unlike
      setLocalLikes(prev => prev - 1);
      setUserVote(null);
    } else {
      // Like (remove dislike if present)
      if (userVote === 'dislike') {
        setLocalDislikes(prev => prev - 1);
      }
      setLocalLikes(prev => prev + 1);
      setUserVote('like');
      triggerAnimation();
    }
    
    if (onLike) onLike(item);
  };

  // Handle optimistic dislike
  const handleDislikeClick = () => {
    if (userVote === 'dislike') {
      // Remove dislike
      setLocalDislikes(prev => prev - 1);
      setUserVote(null);
    } else {
      // Dislike (remove like if present)
      if (userVote === 'like') {
        setLocalLikes(prev => prev - 1);
      }
      setLocalDislikes(prev => prev + 1);
      setUserVote('dislike');
    }
    
    if (onDislike) onDislike(item);
  };

  // Trigger animation for feedback
  const triggerAnimation = () => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);
  };

  // Get author info - handle empty author object
  const author = item.author || {};
  const authorName = author.username || author.name || 'Anonymous';
  const authorAvatar = author.avatar_url;
  const authorReputation = author.total_reputation || 0;

  // Get topic info
  const topic = item.topic || {};
  const topicName = topic.name || '';
  const topicColor = topic.color || '#8B5CF6';
  const topicSlug = topic.slug || '';

  // Time ago
  const timeAgo = shortTimeAgo(item.created_at);

  // Stats
  const likes = item.like_count || 0;
  const dislikes = item.dislike_count || 0;
  const views = item.view_count || 0;
  const answers = item.answer_count || 0;

  // Handle click tracking
  const handleCardClick = () => {
    if (onClick) {
      onClick(item);
    }
  };

  return (
    <article className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200">
      {/* Card wrapper with optimized spacing for engagement */}
      <div className="p-4">
        
        {/* Author Header - Fixed Alignment */}
        <Link 
          to={`/profile/${authorName}`}
          onClick={handleCardClick}
          className="flex items-center gap-3"
        >
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold text-sm shrink-0 hover:ring-2 hover:ring-purple-400 transition-all">
            {authorAvatar ? (
              <img src={authorAvatar} alt={authorName} className="w-full h-full rounded-full object-cover" />
            ) : (
              <span>{authorName.charAt(0).toUpperCase()}</span>
            )}
          </div>

          {/* Author Info - Stacked vertically */}
          <div className="flex flex-col justify-center min-w-0 flex-1">
            {/* Username row */}
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900 hover:text-purple-600 transition-colors truncate text-sm">
                {authorName}
              </span>
              {authorReputation > 100 && (
                <span className="text-xs text-gray-500 font-medium shrink-0">
                  {authorReputation} rep
                </span>
              )}
            </div>
            {/* Topic and time row */}
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {topicName && (
                <>
                  <span
                    className="hover:text-purple-600 transition-colors font-medium truncate"
                    style={{ color: topicColor }}
                  >
                    {topicName}
                  </span>
                  <span className="shrink-0">•</span>
                </>
              )}
              <span className="shrink-0">{timeAgo}</span>
            </div>
          </div>
        </Link>

        {/* Content - Question variant with engagement-optimized hierarchy */}
        {isQuestion && (
          <div className="mt-3 space-y-2">
            <Link to={`/questions/${item.slug}`} onClick={handleCardClick} className="block group">
              {/* Hook/Title - Bold, prominent, scroll-stopping */}
              <h3 className="text-[15px] sm:text-base font-semibold text-gray-900 group-hover:text-purple-600 transition-colors leading-snug mb-1.5">
                {item.title}
              </h3>
              
              {/* Preview - 2 lines max, lighter weight */}
              {item.content && (
                <div className="text-sm text-gray-600 leading-relaxed">
                  {(() => {
                    const { hook, preview } = getContentParts(item.content);
                    const showPreview = hook || preview;
                    
                    return (
                      <>
                        {showPreview && (
                          <p className={`${isExpanded ? '' : 'line-clamp-2'}`}>
                            {isExpanded ? getContentPreview(item.content, 1000) : (hook && preview ? `${hook} ${preview}` : hook || preview)}
                          </p>
                        )}
                        {!isExpanded && showPreview && getContentPreview(item.content, 1000).length > 150 && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setIsExpanded(true);
                            }}
                            className="text-purple-600 hover:text-purple-700 font-medium text-sm mt-1 inline-block"
                          >
                            Read more
                          </button>
                        )}
                        {isExpanded && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setIsExpanded(false);
                            }}
                            className="text-purple-600 hover:text-purple-700 font-medium text-sm mt-1 inline-block"
                          >
                            Show less
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </Link>
            
            {/* Tags - Compact */}
            {item.tags && item.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {item.tags.slice(0, 3).map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-md text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content - Answer variant with engagement focus */}
        {isAnswer && (
          <div className="mt-3 space-y-2.5">
            {/* Question context - Compact */}
            {item.question && (
              <div className="bg-purple-50 border-l-3 border-purple-500 px-2.5 py-1.5 rounded-r">
                <p className="text-[11px] text-purple-700 font-semibold mb-0.5">Answered:</p>
                <Link
                  to={`/questions/${item.question.slug}`}
                  onClick={handleCardClick}
                  className="text-[13px] font-medium text-gray-900 hover:text-purple-600 transition-colors line-clamp-1 leading-snug"
                >
                  {item.question.title}
                </Link>
              </div>
            )}
            
            {/* Answer preview - 2 lines with expand */}
            <Link to={`/questions/${item.question?.slug}#answer-${item.id}`} onClick={handleCardClick} className="block group">
              <div className="text-sm text-gray-600 leading-relaxed">
                <p className={`${isExpanded ? '' : 'line-clamp-2'}`}>
                  {isExpanded ? getContentPreview(item.content, 1000) : getContentPreview(item.content, 150)}
                </p>
                {!isExpanded && getContentPreview(item.content, 1000).length > 150 && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setIsExpanded(true);
                    }}
                    className="text-purple-600 hover:text-purple-700 font-medium text-sm mt-1 inline-block"
                  >
                    Read more
                  </button>
                )}
                {isExpanded && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setIsExpanded(false);
                    }}
                    className="text-purple-600 hover:text-purple-700 font-medium text-sm mt-1 inline-block"
                  >
                    Show less
                  </button>
                )}
              </div>
            </Link>
          </div>
        )}

        {/* Debug Mode - Show ranking scores */}
        {debugMode && item.feed_score !== undefined && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs space-y-2">
            <div className="flex items-center gap-2 font-semibold text-yellow-900">
              <Activity className="w-4 h-4" />
              <span>Debug Info</span>
            </div>
            <div className="text-yellow-800 space-y-1">
              <div>Feed Score: <span className="font-mono font-bold">{item.feed_score?.toFixed(2)}</span></div>
              {item.score_breakdown && (() => {
                const breakdown = item.score_breakdown;
                // Find highest scoring factor
                const factors = [
                  { name: 'Engagement', value: breakdown.engagement, key: 'engagement' },
                  { name: 'Recency', value: breakdown.recency, key: 'recency' },
                  { name: 'Topic', value: breakdown.topic, key: 'topic' },
                  { name: 'Author', value: breakdown.author, key: 'author' }
                ];
                const highest = factors.reduce((max, factor) => 
                  factor.value > max.value ? factor : max
                , factors[0]);
                
                return (
                  <>
                    <div className="pt-1 pb-0.5 text-purple-700 font-semibold text-xs">
                      Primary ranking factor: {highest.name}
                    </div>
                    <div className="ml-2 space-y-0.5 text-xs">
                      {factors.map(factor => (
                        <div 
                          key={factor.key}
                          className={factor.key === highest.key ? 'font-bold text-purple-800' : ''}
                        >
                          • {factor.name}: {factor.value?.toFixed(2)}
                          {factor.key === highest.key && ' ⭐'}
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Action Row - Optimistic UI with smooth animations */}
        <div className="flex items-center gap-1 pt-4 mt-4 border-t border-gray-100">
          {/* Like - With optimistic update and animation */}
          <button
            onClick={handleLikeClick}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all active:scale-95 min-h-[44px] ${
              userVote === 'like'
                ? 'text-purple-600 bg-purple-50'
                : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
            } ${isAnimating ? 'scale-110' : ''}`}
            style={{ transition: 'all 0.2s ease-out' }}
          >
            <ThumbsUp className={`w-4 h-4 ${userVote === 'like' ? 'fill-current' : ''}`} />
            <span className="text-sm font-medium">{localLikes}</span>
          </button>

          {/* Dislike - With optimistic update */}
          <button
            onClick={handleDislikeClick}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all active:scale-95 min-h-[44px] ${
              userVote === 'dislike'
                ? 'text-red-600 bg-red-50'
                : 'text-gray-600 hover:text-red-600 hover:bg-red-50'
            }`}
          >
            <ThumbsDown className={`w-4 h-4 ${userVote === 'dislike' ? 'fill-current' : ''}`} />
          </button>

          {/* Comments/Answers */}
          {isQuestion && answers > 0 && (
            <Link
              to={`/questions/${item.slug}`}
              onClick={handleCardClick}
              className="flex items-center gap-1.5 px-3 py-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all min-h-[44px]"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-sm font-medium">{answers}</span>
            </Link>
          )}

          {/* Views */}
          {views > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-2 text-gray-500 min-h-[44px]">
              <Eye className="w-4 h-4" />
              <span className="text-sm font-medium hidden sm:inline">{views}</span>
            </div>
          )}

          {/* Share */}
          <button
            onClick={() => onShare && onShare(item)}
            className="flex items-center gap-1.5 px-3 py-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all ml-auto min-h-[44px]"
          >
            <Share2 className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Share</span>
          </button>
        </div>
      </div>
    </article>
  );
});

// Display name for debugging
FeedCard.displayName = 'FeedCard';

export default FeedCard;
